import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { PageHero } from '../components/PageHero';
import RCPageHeader from '../components/RCPageHeader';
import { WerkenBijBanner } from '../components/PageBanners';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { vacatures, contactInfo } from '../data/mock';
import { 
  Briefcase, MapPin, Clock, ArrowRight, ArrowLeft, Users, Zap, Heart, Coffee, 
  Euro, ChevronDown, ChevronUp, CheckCircle, GraduationCap, Award, Upload,
  Crown, Mail, Phone, FileText, User, Building, Send, X
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';


const API = "/api";

const WerkenVisual = () => (
  <div className="space-y-3 w-full max-w-xs">
    {[
      { icon: Users, text: 'Hecht en ambitieus team' },
      { icon: GraduationCap, text: '€1.500 opleidingsbudget/jaar' },
      { icon: Zap, text: 'Snelle doorgroeimogelijkheden' },
      { icon: Heart, text: 'Work-life balance & flexibel werken' },
      { icon: Award, text: 'Competitief salaris + bonus' },
    ].map(({ icon: Icon, text }) => (
      <div key={text} className="flex items-center gap-3 bg-white/10 backdrop-blur rounded-xl px-4 py-3">
        <Icon className="w-4 h-4 text-[#D4A017] flex-shrink-0" />
        <span className="text-white text-sm">{text}</span>
      </div>
    ))}
  </div>
);

const WerkenBijPage = () => {
  return (
    <>
      <SEOHead
        title="Werken bij IMPERIA AI — Vacatures in Online Marketing"
        description="Werk bij een van de meest ambitieuze marketing bureaus van Nederland. Bekijk alle openstaande vacatures bij IMPERIA AI in Zwolle: SEO, Google Ads, content en meer."
        keywords="vacatures marketing bureau, werken bij marketing bureau, marketing vacatures zwolle, seo specialist vacature, google ads vacature"
        canonical="/werken-bij"
      />
      <WerkenBijBanner />
      <VacaturesContent />
    </>
  );
};

const VacaturesContent = () => {
  const [expandedVacature, setExpandedVacature] = useState(null);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [selectedVacature, setSelectedVacature] = useState(null);
  const [cvFile, setCvFile] = useState(null);
  const [motivationFile, setMotivationFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    linkedin: '',
    currentEmployer: '',
    currentFunction: '',
    availableFrom: '',
    salaryExpectation: '',
    motivation: '',
    howDidYouFindUs: '',
  });

  const perks = [
    { icon: Users, title: 'Geweldig Team', description: 'Werk met gepassioneerde professionals in een hecht team' },
    { icon: Zap, title: 'Snelle Groei', description: 'Ontwikkel jezelf snel in een dynamische omgeving' },
    { icon: Heart, title: 'Work-Life Balance', description: 'Flexibele werktijden en hybride werkmogelijkheden' },
    { icon: Coffee, title: 'Leuke Extras', description: 'Teamuitjes, goede koffie, lunch en meer' },
    { icon: GraduationCap, title: 'Opleidingen', description: 'Jaarlijks budget voor cursussen en certificeringen' },
    { icon: Award, title: 'Doorgroei', description: 'Duidelijke carrièrepaden en promotiemogelijkheden' },
  ];

  const toggleVacature = (id) => {
    setExpandedVacature(expandedVacature === id ? null : id);
  };

  const openApplicationForm = (vacature) => {
    setSelectedVacature(vacature);
    setShowApplicationForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const closeApplicationForm = () => {
    setShowApplicationForm(false);
    setSelectedVacature(null);
    setCvFile(null);
    setMotivationFile(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      linkedin: '',
      currentEmployer: '',
      currentFunction: '',
      availableFrom: '',
      salaryExpectation: '',
      motivation: '',
      howDidYouFindUs: '',
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCvUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Bestand is te groot. Maximaal 5MB.');
        return;
      }
      if (!['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(file.type)) {
        toast.error('Alleen PDF en Word documenten zijn toegestaan.');
        return;
      }
      setCvFile(file);
      toast.success('CV succesvol geüpload!');
    }
  };

  const handleMotivationUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Bestand is te groot. Maximaal 5MB.');
        return;
      }
      setMotivationFile(file);
      toast.success('Motivatiebrief succesvol geüpload!');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!cvFile) {
      toast.error('Upload je CV om te solliciteren.');
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('vacature', selectedVacature.title);
      formDataToSend.append('firstName', formData.firstName);
      formDataToSend.append('lastName', formData.lastName);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('linkedin', formData.linkedin);
      formDataToSend.append('currentEmployer', formData.currentEmployer);
      formDataToSend.append('currentFunction', formData.currentFunction);
      formDataToSend.append('availableFrom', formData.availableFrom);
      formDataToSend.append('salaryExpectation', formData.salaryExpectation);
      formDataToSend.append('motivation', formData.motivation);
      formDataToSend.append('howDidYouFindUs', formData.howDidYouFindUs);
      formDataToSend.append('cv', cvFile);
      
      if (motivationFile) {
        formDataToSend.append('motivationLetter', motivationFile);
      }

      const response = await axios.post(`${API}/apply`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        toast.success('Sollicitatie succesvol verzonden! We nemen binnen 5 werkdagen contact met je op.');
        closeApplicationForm();
      }
    } catch (error) {
      console.error('Application error:', error);
      toast.error('Er ging iets mis. Probeer het opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Application Form Modal */}
      {showApplicationForm && selectedVacature && (
        <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto">
          <div className="min-h-screen py-8 px-4">
            <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl">
              {/* Form Header */}
              <div className="bg-[#D4A017] rounded-t-2xl p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-[#0f172a] mb-2">
                      Solliciteren: {selectedVacature.title}
                    </h2>
                    <div className="flex items-center gap-4 text-[#0f172a]/70 text-sm">
                      <span className="flex items-center gap-1">
                        <MapPin size={14} />
                        {selectedVacature.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <Euro size={14} />
                        {selectedVacature.salary}
                      </span>
                    </div>
                  </div>
                  <button 
                    onClick={closeApplicationForm}
                    className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center hover:bg-white/30 transition-colors"
                  >
                    <X className="w-5 h-5 text-[#0f172a]" />
                  </button>
                </div>
              </div>

              {/* Form Content */}
              <form onSubmit={handleSubmit} className="p-8">
                <div className="space-y-8">
                  {/* Personal Info Section */}
                  <div>
                    <h3 className="text-lg font-bold text-[#0f172a] mb-4 flex items-center gap-2">
                      <User className="w-5 h-5 text-[#D4A017]" />
                      Persoonlijke Gegevens
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[#0f172a] mb-2">
                          Voornaam *
                        </label>
                        <Input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          placeholder="Je voornaam"
                          required
                          className="bg-white border-[#e2e8f0]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#0f172a] mb-2">
                          Achternaam *
                        </label>
                        <Input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          placeholder="Je achternaam"
                          required
                          className="bg-white border-[#e2e8f0]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#0f172a] mb-2">
                          Email *
                        </label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="je@email.nl"
                          required
                          className="bg-white border-[#e2e8f0]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#0f172a] mb-2">
                          Telefoonnummer *
                        </label>
                        <Input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="06-12345678"
                          required
                          className="bg-white border-[#e2e8f0]"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-[#0f172a] mb-2">
                          LinkedIn Profiel
                        </label>
                        <Input
                          type="url"
                          name="linkedin"
                          value={formData.linkedin}
                          onChange={handleChange}
                          placeholder="https://linkedin.com/in/jouwprofiel"
                          className="bg-white border-[#e2e8f0]"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Current Position Section */}
                  <div>
                    <h3 className="text-lg font-bold text-[#0f172a] mb-4 flex items-center gap-2">
                      <Building className="w-5 h-5 text-[#D4A017]" />
                      Huidige Situatie
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[#0f172a] mb-2">
                          Huidige Werkgever
                        </label>
                        <Input
                          type="text"
                          name="currentEmployer"
                          value={formData.currentEmployer}
                          onChange={handleChange}
                          placeholder="Naam van je huidige werkgever"
                          className="bg-white border-[#e2e8f0]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#0f172a] mb-2">
                          Huidige Functie
                        </label>
                        <Input
                          type="text"
                          name="currentFunction"
                          value={formData.currentFunction}
                          onChange={handleChange}
                          placeholder="Je huidige functietitel"
                          className="bg-white border-[#e2e8f0]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#0f172a] mb-2">
                          Beschikbaar vanaf *
                        </label>
                        <Input
                          type="date"
                          name="availableFrom"
                          value={formData.availableFrom}
                          onChange={handleChange}
                          required
                          className="bg-white border-[#e2e8f0]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#0f172a] mb-2">
                          Salarisverwachting
                        </label>
                        <Input
                          type="text"
                          name="salaryExpectation"
                          value={formData.salaryExpectation}
                          onChange={handleChange}
                          placeholder="Bijv. €3.500 bruto/maand"
                          className="bg-white border-[#e2e8f0]"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Documents Section */}
                  <div>
                    <h3 className="text-lg font-bold text-[#0f172a] mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-[#D4A017]" />
                      Documenten
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[#0f172a] mb-2">
                          CV Uploaden * (PDF of Word, max 5MB)
                        </label>
                        <div className="relative">
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={handleCvUpload}
                            className="hidden"
                            id="cv-upload"
                          />
                          <label
                            htmlFor="cv-upload"
                            className={`flex items-center justify-center gap-2 w-full h-24 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                              cvFile 
                                ? 'border-green-500 bg-green-50 text-green-700' 
                                : 'border-[#e2e8f0] hover:border-[#D4A017] hover:bg-[#D4A017]/5'
                            }`}
                          >
                            {cvFile ? (
                              <>
                                <CheckCircle className="w-6 h-6" />
                                <span className="font-medium">{cvFile.name}</span>
                              </>
                            ) : (
                              <>
                                <Upload className="w-6 h-6 text-[#64748b]" />
                                <span className="text-[#64748b]">Klik om CV te uploaden</span>
                              </>
                            )}
                          </label>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#0f172a] mb-2">
                          Motivatiebrief (optioneel)
                        </label>
                        <div className="relative">
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={handleMotivationUpload}
                            className="hidden"
                            id="motivation-upload"
                          />
                          <label
                            htmlFor="motivation-upload"
                            className={`flex items-center justify-center gap-2 w-full h-24 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                              motivationFile 
                                ? 'border-green-500 bg-green-50 text-green-700' 
                                : 'border-[#e2e8f0] hover:border-[#D4A017] hover:bg-[#D4A017]/5'
                            }`}
                          >
                            {motivationFile ? (
                              <>
                                <CheckCircle className="w-6 h-6" />
                                <span className="font-medium">{motivationFile.name}</span>
                              </>
                            ) : (
                              <>
                                <Upload className="w-6 h-6 text-[#64748b]" />
                                <span className="text-[#64748b]">Klik om brief te uploaden</span>
                              </>
                            )}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Motivation Section */}
                  <div>
                    <h3 className="text-lg font-bold text-[#0f172a] mb-4 flex items-center gap-2">
                      <Mail className="w-5 h-5 text-[#D4A017]" />
                      Motivatie
                    </h3>
                    <div>
                      <label className="block text-sm font-medium text-[#0f172a] mb-2">
                        Waarom wil je bij IMPERIA AI werken? *
                      </label>
                      <Textarea
                        name="motivation"
                        value={formData.motivation}
                        onChange={handleChange}
                        placeholder="Vertel ons waarom je geïnteresseerd bent in deze functie en wat je kunt bijdragen aan ons team..."
                        required
                        rows={5}
                        className="bg-white border-[#e2e8f0] resize-none"
                      />
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-[#0f172a] mb-2">
                        Hoe heb je ons gevonden?
                      </label>
                      <select
                        name="howDidYouFindUs"
                        value={formData.howDidYouFindUs}
                        onChange={handleChange}
                        className="w-full h-10 px-3 rounded-md border border-[#e2e8f0] bg-white text-[#0f172a]"
                      >
                        <option value="">Selecteer een optie</option>
                        <option value="google">Google</option>
                        <option value="linkedin">LinkedIn</option>
                        <option value="indeed">Indeed</option>
                        <option value="referral">Via bekende</option>
                        <option value="social">Social Media</option>
                        <option value="website">Website</option>
                        <option value="other">Anders</option>
                      </select>
                    </div>
                  </div>

                  {/* Submit Section */}
                  <div className="pt-6 border-t border-gray-100">
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex-1 bg-[#0f172a] hover:bg-[#1e293b] text-white py-6 text-lg"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center gap-2">
                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Verzenden...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2 justify-center">
                            Sollicitatie Versturen
                            <Send size={18} />
                          </span>
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={closeApplicationForm}
                        className="border-[#0f172a] text-[#0f172a] hover:bg-gray-100 py-6"
                      >
                        Annuleren
                      </Button>
                    </div>
                    <p className="text-xs text-[#64748b] text-center mt-4">
                      Door te solliciteren ga je akkoord met onze privacyverklaring. 
                      We gebruiken je gegevens alleen voor het sollicitatieproces.
                    </p>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="bg-[#D4A017] py-16">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block text-sm font-semibold text-[#0f172a]/70 uppercase tracking-wider mb-4">
              Carrière
            </span>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-[#0f172a] mb-6">
              Word onderdeel van het IMPERIA AI
            </h1>
            <p className="text-lg text-[#1e293b] leading-relaxed mb-4">
              We zijn altijd op zoek naar talent dat net zo gepassioneerd is over marketing als wij. 
              Bij IMPERIA AI werk je aan uitdagende projecten voor diverse klanten, van startups tot grote corporates.
            </p>
            <p className="text-[#0f172a]">
              <span className="font-semibold text-2xl">{vacatures.length}</span> openstaande vacatures
            </p>
          </div>
        </div>
      </section>

      {/* Perks Section */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#0f172a] mb-8 text-center">
            Waarom werken bij IMPERIA AI?
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {perks.map((perk, index) => {
              const IconComponent = perk.icon;
              return (
                <div key={index} className="bg-[#f8fafc] rounded-xl p-6 text-center card-hover">
                  <div className="w-12 h-12 bg-[#D4A017] rounded-lg flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="w-6 h-6 text-[#0f172a]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#0f172a] mb-2">{perk.title}</h3>
                  <p className="text-sm text-[#64748b]">{perk.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Vacatures Section */}
      <section className="py-16 bg-[#f8fafc]">
        <div className="container-custom">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#0f172a] mb-8 text-center">
            Openstaande Vacatures
          </h2>
          
          <div className="space-y-4 max-w-4xl mx-auto">
            {vacatures.map((vacature) => (
              <Card
                key={vacature.id}
                className="bg-white border-none overflow-hidden shadow-md"
              >
                <CardContent className="p-0">
                  {/* Vacature Header */}
                  <div 
                    className="p-6 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                    onClick={() => toggleVacature(vacature.id)}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-[#0f172a] rounded-lg flex items-center justify-center flex-shrink-0">
                          <Briefcase className="w-6 h-6 text-[#D4A017]" />
                        </div>
                        <div>
                          <h4 className="text-xl font-bold text-[#0f172a] mb-1">
                            {vacature.title}
                          </h4>
                          <div className="flex flex-wrap items-center gap-3 text-sm text-[#64748b]">
                            <span className="flex items-center gap-1">
                              <MapPin size={14} />
                              {vacature.location}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock size={14} />
                              {vacature.type}
                            </span>
                            <span className="flex items-center gap-1">
                              <Euro size={14} />
                              {vacature.salary}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-medium bg-[#D4A017]/20 text-[#B8860B] px-3 py-1 rounded-full">
                          {vacature.type}
                        </span>
                        {expandedVacature === vacature.id ? (
                          <ChevronUp className="w-5 h-5 text-[#64748b]" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-[#64748b]" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {expandedVacature === vacature.id && (
                    <div className="px-6 pb-6 border-t border-gray-100 pt-6 animate-fade-in">
                      <div className="grid lg:grid-cols-3 gap-8">
                        {/* Description & Responsibilities */}
                        <div className="lg:col-span-2 space-y-6">
                          <div>
                            <h5 className="font-bold text-[#0f172a] mb-3">Over deze functie</h5>
                            <p className="text-[#64748b] leading-relaxed">
                              {vacature.description}
                            </p>
                          </div>

                          <div>
                            <h5 className="font-bold text-[#0f172a] mb-3">Wat ga je doen?</h5>
                            <ul className="space-y-2">
                              {vacature.responsibilities.map((resp, i) => (
                                <li key={i} className="flex items-start gap-2 text-[#64748b]">
                                  <CheckCircle className="w-5 h-5 text-[#D4A017] flex-shrink-0 mt-0.5" />
                                  <span>{resp}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          <div>
                            <h5 className="font-bold text-[#0f172a] mb-3">Wat verwachten wij?</h5>
                            <ul className="space-y-2">
                              {vacature.requirements.map((req, i) => (
                                <li key={i} className="flex items-start gap-2 text-[#64748b]">
                                  <div className="w-1.5 h-1.5 bg-[#0f172a] rounded-full flex-shrink-0 mt-2" />
                                  <span>{req}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>

                        {/* Benefits & Apply */}
                        <div className="space-y-6">
                          <div className="bg-[#f8fafc] rounded-xl p-6">
                            <h5 className="font-bold text-[#0f172a] mb-3">Wat bieden wij?</h5>
                            <ul className="space-y-2">
                              {vacature.benefits.map((benefit, i) => (
                                <li key={i} className="flex items-start gap-2 text-[#64748b] text-sm">
                                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                  <span>{benefit}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          <div className="bg-[#0f172a] rounded-xl p-6 text-center">
                            <h5 className="font-bold text-white mb-2">Interesse?</h5>
                            <p className="text-[#94a3b8] text-sm mb-4">
                              Solliciteer direct via ons online formulier met CV upload.
                            </p>
                            <Button
                              onClick={() => openApplicationForm(vacature)}
                              className="w-full bg-[#D4A017] text-[#0f172a] hover:bg-[#B8860B] btn-hover"
                            >
                              Solliciteer Nu
                              <ArrowRight size={16} className="ml-2" />
                            </Button>
                          </div>

                          <div className="text-center text-sm text-[#64748b]">
                            <p>Of stuur je CV naar:</p>
                            <a href="mailto:jobs@imperiaai.nl" className="text-[#0f172a] font-semibold hover:underline">
                              jobs@imperiaai.nl
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Open Application CTA */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto bg-[#D4A017] rounded-2xl p-8 md:p-12">
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-[#0f172a] mb-4">
              Geen passende vacature gevonden?
            </h3>
            <p className="text-[#1e293b] mb-6">
              Stuur ons een open sollicitatie! We zijn altijd geïnteresseerd in getalenteerde mensen die ons team willen versterken. 
              Vertel ons over jezelf en wat jij kunt bijdragen aan IMPERIA AI.
            </p>
            <Button
              onClick={() => openApplicationForm({ 
                id: 0, 
                title: 'Open Sollicitatie', 
                location: 'Zwolle', 
                salary: 'In overleg' 
              })}
              className="bg-[#0f172a] hover:bg-[#1e293b] text-white px-8 py-6 text-lg rounded-md btn-hover"
            >
              Stuur Open Sollicitatie
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-12 bg-[#0f172a]">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="text-white font-bold text-lg mb-2">Vragen over werken bij IMPERIA AI?</h3>
              <p className="text-[#94a3b8]">Neem contact op met ons HR team</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={`mailto:jobs@imperiaai.nl`} className="flex items-center gap-2 text-[#D4A017] hover:text-white transition-colors">
                <Mail size={18} />
                jobs@imperiaai.nl
              </a>
              <a href={`tel:${contactInfo.phone}`} className="flex items-center gap-2 text-[#D4A017] hover:text-white transition-colors">
                <Phone size={18} />
                {contactInfo.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WerkenBijPage;
